import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';
import SelectionButtonGroup from './SelectionButtonGroup';

jest.mock('components/styleguide/icon/Icon', () => ({
  __esModule: true,
  default: ({ name, ...props }: any) => <span data-icon={name} {...props} />,
}));

jest.mock('components/styleguide/input_field/InputField', () => ({
  __esModule: true,
  default: () => null,
}));

const options = [
  { id: 'ft', title: 'Full-time', description: 'Permanent position', icon: 'briefcaseBusiness', value: 'full_time' },
  { id: 'pt', title: 'Part-time', description: 'Flexible hours', icon: 'clock', value: 'part_time' },
  { id: 'fl', title: 'Freelance', description: 'Project-based', icon: 'user', value: 'freelance' },
];

describe('SelectionButtonGroup', () => {
  it('renders all options', () => {
    render(<SelectionButtonGroup options={options} />);
    expect(screen.getByText('Full-time')).toBeInTheDocument();
    expect(screen.getByText('Part-time')).toBeInTheDocument();
    expect(screen.getByText('Freelance')).toBeInTheDocument();
  });

  it('selects an option on click', () => {
    const { container } = render(<SelectionButtonGroup options={options} />);
    fireEvent.click(screen.getByText('Full-time').closest('[role="radio"]')!);
    expect(container.querySelector('.selected')).toBeInTheDocument();
  });

  it('calls onSelectionChange with the value', () => {
    const onChange = jest.fn();
    render(<SelectionButtonGroup options={options} onSelectionChange={onChange} />);
    fireEvent.click(screen.getByText('Part-time').closest('[role="radio"]')!);
    expect(onChange).toHaveBeenCalledWith('part_time');
  });

  it('only one option is selected at a time', () => {
    const { container } = render(<SelectionButtonGroup options={options} />);
    fireEvent.click(screen.getByText('Full-time').closest('[role="radio"]')!);
    fireEvent.click(screen.getByText('Part-time').closest('[role="radio"]')!);
    expect(container.querySelectorAll('.selected')).toHaveLength(1);
  });

  it('renders hidden input with selected value when inputName is provided', () => {
    const { container } = render(<SelectionButtonGroup options={options} inputName="job_type" />);
    fireEvent.click(screen.getByText('Freelance').closest('[role="radio"]')!);
    const hidden = container.querySelector('input[type="hidden"]') as HTMLInputElement;
    expect(hidden).toHaveAttribute('name', 'job_type');
    expect(hidden.value).toBe('freelance');
  });

  it('hidden input is empty when nothing selected', () => {
    const { container } = render(<SelectionButtonGroup options={options} inputName="job_type" />);
    const hidden = container.querySelector('input[type="hidden"]') as HTMLInputElement;
    expect(hidden.value).toBe('');
  });

  it('sets required on hidden input when required', () => {
    const { container } = render(<SelectionButtonGroup options={options} inputName="job_type" required />);
    expect(container.querySelector('input[type="hidden"]')).toHaveAttribute('required');
  });

  describe('accessibility', () => {
    it('has role="radiogroup"', () => {
      render(<SelectionButtonGroup options={options} />);
      expect(screen.getByRole('radiogroup')).toBeInTheDocument();
    });

    it('has default aria-label', () => {
      render(<SelectionButtonGroup options={options} />);
      expect(screen.getByRole('radiogroup')).toHaveAttribute('aria-label', 'Select an option');
    });

    it('supports custom aria-label', () => {
      render(<SelectionButtonGroup options={options} label="Job type" />);
      expect(screen.getByRole('radiogroup')).toHaveAttribute('aria-label', 'Job type');
    });

    it('sets aria-required when required', () => {
      render(<SelectionButtonGroup options={options} required />);
      expect(screen.getByRole('radiogroup')).toHaveAttribute('aria-required', 'true');
    });

    it('does not set aria-required when not required', () => {
      render(<SelectionButtonGroup options={options} />);
      expect(screen.getByRole('radiogroup')).not.toHaveAttribute('aria-required');
    });
  });

  it('applies custom className', () => {
    const { container } = render(<SelectionButtonGroup options={options} className="custom" />);
    expect(container.firstChild).toHaveClass('custom');
  });
});
